import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  CheckCircle,
  XCircle,
  X,
  FileSpreadsheet,
  ImageIcon,
  ChevronUp,
} from 'lucide-react';
import { api } from '../../api/client';
import {
  useBackgroundTaskStore,
  type BackgroundTask,
} from '../../store/useBackgroundTaskStore';

export function BackgroundTaskIndicator() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const pollingRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const tasks = useBackgroundTaskStore((s) => s.tasks);
  const updateTask = useBackgroundTaskStore((s) => s.updateTask);
  const removeTask = useBackgroundTaskStore((s) => s.removeTask);

  // Stable set of active task IDs (only changes when IDs actually change)
  const activeTaskIds = useMemo(() => {
    const ids = tasks
      .filter((t) => !['completed', 'failed', 'cancelled'].includes(t.status))
      .map((t) => t.id);
    return ids.join(',');
  }, [tasks]);

  const hasActive = activeTaskIds.length > 0;

  // Auto-remove completed tasks after 30 seconds
  // Use task IDs + statuses as stable dependency
  const completedTaskIds = useMemo(() => {
    return tasks
      .filter((t) => ['completed', 'failed', 'cancelled'].includes(t.status))
      .map((t) => t.id)
      .join(',');
  }, [tasks]);

  useEffect(() => {
    if (!completedTaskIds) return;
    const ids = completedTaskIds.split(',');
    const timers = ids.map((id) =>
      setTimeout(() => {
        useBackgroundTaskStore.getState().removeTask(id);
      }, 30000)
    );
    return () => timers.forEach(clearTimeout);
  }, [completedTaskIds]);

  // Poll active tasks — uses stable string of IDs as dependency
  useEffect(() => {
    if (!activeTaskIds) return;

    const ids = activeTaskIds.split(',');
    const currentPolling = pollingRef.current;

    const pollTaskById = async (taskId: string) => {
      const task = useBackgroundTaskStore.getState().tasks.find((t) => t.id === taskId);
      if (!task) return;

      try {
        const endpoint =
          task.type === 'csv-import'
            ? `/articles/import/job/${task.id}`
            : `/image-import/job/${task.id}`;

        if (task.type === 'csv-import') {
          const response = await api.fetch(endpoint);
          const result = await response.json();
          if (!result.success || !result.job) return;
          const job = result.job;

          useBackgroundTaskStore.getState().updateTask(task.id, {
            status: job.status === 'queued' ? 'queued' : job.status === 'completed' ? 'completed' : job.status === 'failed' ? 'failed' : job.status === 'cancelled' ? 'cancelled' : 'processing',
            progress: job.progress || 0,
            processedItems: job.processedRows || 0,
            totalItems: job.totalRows || task.totalItems,
            result: ['completed', 'failed', 'cancelled'].includes(job.status)
              ? {
                  imported: job.importedArticles || 0,
                  updated: job.updatedArticles || 0,
                  skipped: job.skippedRows || 0,
                  errors: job.errorRows || 0,
                }
              : undefined,
          });
        } else {
          const response = await api.get(endpoint);
          if (!response.success || !response.job) return;
          const job = response.job as Record<string, any>;

          useBackgroundTaskStore.getState().updateTask(task.id, {
            status: job.status === 'completed' ? 'completed' : job.status === 'failed' ? 'failed' : job.status === 'cancelled' ? 'cancelled' : 'processing',
            progress: job.progress || 0,
            processedItems: job.processedFiles || 0,
            totalItems: job.totalFiles || task.totalItems,
            result: ['completed', 'failed', 'cancelled'].includes(job.status)
              ? {
                  uploadedImages: job.uploadedImages || 0,
                  skipped: job.skippedFiles || 0,
                  failedFiles: job.failedFiles || 0,
                }
              : undefined,
          });
        }
      } catch {
        // Don't stop polling on single error
      }
    };

    // Start polling for each active task
    for (const id of ids) {
      if (!currentPolling.has(id)) {
        // Initial poll (delayed to avoid sync loop)
        setTimeout(() => pollTaskById(id), 500);
        // Set interval
        const interval = setInterval(() => pollTaskById(id), 5000);
        currentPolling.set(id, interval);
      }
    }

    // Stop polling for tasks that are no longer active
    const activeIdSet = new Set(ids);
    for (const [id, interval] of currentPolling.entries()) {
      if (!activeIdSet.has(id)) {
        clearInterval(interval);
        currentPolling.delete(id);
      }
    }

    return () => {
      for (const interval of currentPolling.values()) {
        clearInterval(interval);
      }
      currentPolling.clear();
    };
  }, [activeTaskIds]);

  // Close panel on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Nothing to show
  if (tasks.length === 0) return null;

  const getTaskIcon = (task: BackgroundTask) => {
    if (task.type === 'csv-import') return <FileSpreadsheet size={16} />;
    return <ImageIcon size={16} />;
  };

  const getStatusIcon = (task: BackgroundTask) => {
    if (task.status === 'completed')
      return <CheckCircle size={16} className="text-green-500" />;
    if (task.status === 'failed' || task.status === 'cancelled')
      return <XCircle size={16} className="text-red-500" />;
    return <Loader2 size={16} className="animate-spin text-blue-500" />;
  };

  const getStatusText = (task: BackgroundTask) => {
    if (task.status === 'completed') return t('backgroundTasks.completed', 'Abgeschlossen');
    if (task.status === 'failed') return t('backgroundTasks.failed', 'Fehlgeschlagen');
    if (task.status === 'cancelled') return t('backgroundTasks.failed', 'Fehlgeschlagen');
    return `${task.progress.toFixed(0)}%`;
  };

  const getResultSummary = (task: BackgroundTask) => {
    if (!task.result) return null;
    const parts: string[] = [];
    if (task.type === 'csv-import') {
      if (task.result.imported) parts.push(`${task.result.imported} ${t('backgroundTasks.imported', 'importiert')}`);
      if (task.result.updated) parts.push(`${task.result.updated} ${t('backgroundTasks.updated', 'aktualisiert')}`);
      if (task.result.errors) parts.push(`${task.result.errors} ${t('backgroundTasks.errors', 'Fehler')}`);
    } else {
      if (task.result.uploadedImages) parts.push(`${task.result.uploadedImages} ${t('backgroundTasks.imported', 'importiert')}`);
      if (task.result.skipped) parts.push(`${task.result.skipped} ${t('backgroundTasks.skipped', 'übersprungen')}`);
      if (task.result.failedFiles) parts.push(`${task.result.failedFiles} ${t('backgroundTasks.errors', 'Fehler')}`);
    }
    return parts.join(', ');
  };

  return (
    <div ref={panelRef} className="fixed bottom-6 right-20 z-40">
      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute bottom-14 right-0 w-80 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-fade-in">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-700">
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
              {t('backgroundTasks.title', 'Hintergrundaufgaben')}
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400"
            >
              <ChevronUp size={16} />
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {tasks.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-400">
                {t('backgroundTasks.noTasks', 'Keine aktiven Aufgaben')}
              </div>
            ) : (
              tasks.map((task) => (
                <div
                  key={task.id}
                  className="px-4 py-3 border-b border-gray-50 dark:border-gray-700/50 last:border-0"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-gray-500 dark:text-gray-400">
                      {getTaskIcon(task)}
                    </span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate flex-1">
                      {task.label}
                    </span>
                    {getStatusIcon(task)}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mb-1">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${
                        task.status === 'completed'
                          ? 'bg-green-500'
                          : task.status === 'failed' || task.status === 'cancelled'
                            ? 'bg-red-500'
                            : 'bg-blue-500'
                      }`}
                      style={{ width: `${Math.min(task.progress, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {['completed', 'failed', 'cancelled'].includes(task.status)
                        ? getResultSummary(task) || getStatusText(task)
                        : `${task.processedItems}/${task.totalItems} — ${getStatusText(task)}`}
                    </span>
                    {['completed', 'failed', 'cancelled'].includes(task.status) && (
                      <button
                        onClick={() => removeTask(task.id)}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1"
                      >
                        <X size={12} />
                        {t('backgroundTasks.dismiss', 'Schließen')}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Floating badge button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-105 ${
          hasActive
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
        title={t('backgroundTasks.title', 'Hintergrundaufgaben')}
      >
        {hasActive ? (
          <Loader2 size={22} className="animate-spin" />
        ) : (
          <CheckCircle size={22} />
        )}
        {/* Count badge */}
        {tasks.length > 0 && (
          <span
            className={`absolute -top-1 -right-1 flex items-center justify-center min-w-5 h-5 px-1 text-[11px] font-bold text-white rounded-full ${
              hasActive ? 'bg-red-500' : 'bg-green-500'
            }`}
          >
            {tasks.length}
          </span>
        )}
      </button>
    </div>
  );
}
